import { useState, type ComponentProps, type ReactElement, type ReactNode } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * A task decision behind a confirmation (Tasks page: review verdicts,
 * contract-change approve/decline). `trigger` is the button that opens it;
 * `onConfirm` resolves to whether the call succeeded, so the dialog closes
 * only on success and stays open (with the error toast) on failure — the
 * PayoutDecision pattern.
 */
export default function ConfirmActionDialog({
  trigger,
  title,
  description,
  children,
  confirmLabel,
  confirmIcon,
  confirmVariant = "neutral",
  isLoading,
  onConfirm,
}: {
  trigger: ReactElement;
  title: string;
  description: ReactNode;
  /** Optional preview of what's being confirmed (wording, notes…). */
  children?: ReactNode;
  confirmLabel: string;
  confirmIcon: ReactNode;
  confirmVariant?: ComponentProps<typeof Button>["variant"];
  isLoading: boolean;
  onConfirm: () => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);

  const run = async () => {
    if (await onConfirm()) setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button variant={confirmVariant} disabled={isLoading} onClick={run}>
            {isLoading ? <Spinner /> : confirmIcon}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
